import Label from "@/components/forms/Label"
import Slider from "@/components/forms/Slider"
import { useState, useEffect } from "react"
import cx from "classnames"

/**
 * BalanceTab Component
 *
 * Manages balance settings with:
 * - Offset adjustment (-15 to +15)
 */
type BalanceTabProps = {
  eqSettings: any
  mode: string
  onEqChange: (changes: any[]) => void
  defaultSettings: any
}

const BalanceTab = ({ eqSettings, onEqChange, mode, defaultSettings }: BalanceTabProps) => {
  const [balanceOffset, setBalanceOffset] = useState<number>(0)
  const [displayValue, setDisplayValue] = useState<number>(0)

  /**
   * On settings change
   *
   * When parent settings change, update balance settings
   */
  useEffect(() => {
    if (eqSettings) {
      // Get default balance settings based on current mode
      const defaultBalanceSettings =
        defaultSettings.Configuration.DSP[mode]?.Balance ||
        defaultSettings.Configuration.DSP.Headphone.Balance

      // Handle missing Balance settings in older presets
      const balanceSettings = eqSettings["Balance"] || defaultBalanceSettings

      const newValue = parseFloat(balanceSettings["Offset"]["Current"])
      setBalanceOffset(newValue)
      // Invert the value for the slider display (so left = positive, right = negative)
      setDisplayValue(-newValue)
    }
  }, [eqSettings, mode])

  /**
   * Update balance settings
   *
   * @param {*} domain
   * @param {*} value
   * @returns
   */
  const update = async (domain, value) => {
    try {
      await onEqChange([["Balance", domain, value]])
      return true
    } catch (error) {
      // If the device update fails, revert to the current value (inverted for display)
      setDisplayValue(-balanceOffset)
      return false
    }
  }

  const handleSliderChange = (value) => {
    setDisplayValue(value)
  }

  const handleSliderRelease = async (value) => {
    // Invert the slider value back to the actual device value
    await update("Offset", -value)
  }

  // Get settings with fallbacks for older presets
  const defaultBalanceSettings =
    defaultSettings.Configuration.DSP[mode]?.Balance ||
    defaultSettings.Configuration.DSP.Headphone.Balance
  const balanceSettings = eqSettings?.["Balance"] || defaultBalanceSettings

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label
          text="Balance Offset"
          info={
            <div className="max-w-md space-y-1">
              <p>
                Adjusts the left/right channel balance. Positive values favor the right channel,
                negative values favor the left channel.
              </p>
              <p>Range: +/- 15 dB in 0.5 dB steps</p>
              <p>
                Note: Balance settings are not saved in Cloud presets, but will be saved with any
                Profile stored directly on your Element IV.
              </p>
            </div>
          }
        />
        <div className="flex max-w-xl items-center gap-x-4">
          <div className="relative flex-1">
            <Slider
              value={displayValue}
              onChange={handleSliderChange}
              onDebouncedChange={handleSliderRelease}
              onPointerUp={() => handleSliderRelease(displayValue)}
              minValue={-balanceSettings["Offset"]["Max"]}
              maxValue={-balanceSettings["Offset"]["Min"]}
              step={0.5}
              formatValue={(value) => value.toString()}
              className="relative z-10 flex-1"
            />
            {/* Center line indicator */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-6 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-gray-400"></div>
            {/* Center label */}
            <div className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 text-xs text-gray-500">
              Center
            </div>
          </div>
          <div className="w-24 text-center text-sm">Value: {displayValue}</div>
        </div>
      </div>
    </div>
  )
}

export default BalanceTab
